import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DashboardClient from '../data/api/DashboardClient';
import JobDashboardClient, { BaseMetricsAgg } from '../../jobDashboard/data/api/JobDashboardClient';
import {
  Users,
  CheckCircle,
  Clock,
  CalendarDays,
  Sparkles,
  FolderOpen,
  AlertTriangle,
  Bell,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Pin,
  Star,
  Calendar,
  Search,
  Video,
  ClipboardList,
  ArrowRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import Loader from '../../../shared/components/Loader';
import Modal from '../../../shared/components/Modal';
import JobNotificationToast from '../../../shared/components/JobNotificationToast';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import '../styles/DashboardStyles.css';

const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#ef4444', '#14b8a6', '#f43f5e', '#84cc16'];
const DEFAULT_STAGES = ['Applied', 'Shortlisted', 'Interviewed', 'Offered', 'Accepted', 'Declined', 'Rejected'];

const formatCalTime = (timeStr: string) => {
  if (!timeStr) return '';
  const timePart = timeStr.includes(',') ? timeStr.split(',')[1].trim() : timeStr;
  const match = timePart.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampmMatch = timePart.match(/AM|PM/i);
    let ampm = ampmMatch ? ampmMatch[0].toUpperCase() : null;
    if (!ampm) {
      ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
    }
    return `${hours}:${minutes} ${ampm}`;
  }
  return timePart;
};

const parseScheduleDate = (dtStr: string): Date | null => {
  if (!dtStr) return null;
  try {
    const parts = dtStr.split(',');
    const datePart = parts[0].trim();
    const segments = datePart.split('/');
    if (segments.length === 3) {
      let y: number, m: number, d: number;
      if (segments[0].length === 4) {
        [y, m, d] = [parseInt(segments[0]), parseInt(segments[1]) - 1, parseInt(segments[2])];
      } else {
        [d, m, y] = [parseInt(segments[0]), parseInt(segments[1]) - 1, parseInt(segments[2])];
      }
      return new Date(y, m, d);
    }
  } catch { }
  return null;
};

const FrostedGlassStatCard = ({
  icon, heading, count, accent = 'default', onClick,
}: { icon: React.ReactNode; heading: string; count: React.ReactNode; accent?: string; onClick?: () => void }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2, cy = rect.height / 2;
      card.style.transform = `perspective(1000px) rotateX(${((y - cy) / cy) * -6}deg) rotateY(${((x - cx) / cx) * 6}deg) scale3d(1.02,1.02,1.02)`;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    };
    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    };
    card.addEventListener('mousemove', handleMouseMove as any);
    card.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      card.removeEventListener('mousemove', handleMouseMove as any);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const clickable = typeof onClick === 'function';

  return (
    <div className={`glass-card-container accent-${accent}${clickable ? ' glass-clickable' : ''}`}>
      <div
        ref={cardRef}
        className="glass-card"
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={onClick}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
        title={clickable ? `View ${heading.toLowerCase()}` : undefined}
      >
        <div className="glass-row" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="glass-icon-wrapper">{icon}</div>
            <h2 className="glass-heading">{heading.toUpperCase()}</h2>
          </div>
          <div className="glass-count-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <h3 className="glass-count">{count}</h3>
            {clickable && <ChevronRight size={16} className="glass-go" />}
          </div>
        </div>
        <div className="glass-glare" />
      </div>
    </div>
  );
};

// ─── Schedule type picker (Interviews vs Assessments) ────────────────────────
const SchedulePickerModal = ({
  open, onClose, onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (target: 'interviews' | 'assessments') => void;
}) => {
  return (
    <Modal open={open} onClose={onClose} title="View Scheduled Events" width="560px">
      <p className="sched-picker-sub">Choose which schedule list to open</p>
      <div className="sched-picker-grid">
        <button
          type="button"
          className="sched-picker-option accent-blue"
          onClick={() => onSelect('interviews')}
        >
          <div className="sched-picker-icon"><Video size={22} /></div>
          <div className="sched-picker-title">Interviews</div>
          <div className="sched-picker-desc">AI &amp; expert interview sessions</div>
          <div className="sched-picker-go">Open interviews <ArrowRight size={14} /></div>
        </button>
        <button
          type="button"
          className="sched-picker-option accent-purple"
          onClick={() => onSelect('assessments')}
        >
          <div className="sched-picker-icon"><ClipboardList size={22} /></div>
          <div className="sched-picker-title">Assessments</div>
          <div className="sched-picker-desc">Coding &amp; skill assessments</div>
          <div className="sched-picker-go">Open assessments <ArrowRight size={14} /></div>
        </button>
      </div>
    </Modal>
  );
};

const NoticeBoardCard = ({ actionRequired, notifications }: { actionRequired: any[]; notifications: any[] }) => {
  return (
    <div className="sticky-note-wrapper">
      <div className="sticky-note-card">
        <div className="sn-curl" />

        {/* Header */}
        <div className="sn-header-row">
          <div className="sn-header-title">
            <Bell size={20} color="#78350f" strokeWidth={2.5} />
            Notice Board
          </div>
          <span className="sn-live-badge">LIVE ALERTS</span>
        </div>
        <div className="sn-divider" />

        <div className="sn-scroll-area">
          {/* Action Required */}
          <div className="sn-section-title" style={{ color: '#9f1239' }}>
            <AlertTriangle size={15} color="#e11d48" strokeWidth={2.5} />
            ACTION REQUIRED
          </div>
          <div className="sn-list">
            {actionRequired.length === 0 ? (
              <div className="sn-item sn-notif-item">✅ All caught up!</div>
            ) : (
              actionRequired.map((item: any, idx: number) => (
                <div key={idx} className="sn-item sn-action-item" style={{ borderLeftColor: idx === 0 ? '#f43f5e' : '#f59e0b' }}>
                  <div className="sn-item-content">
                    <span className="sn-item-msg">{item.message}</span>
                    {item.sub && <span className="sn-item-sub" style={{ color: idx === 0 ? '#e11d48' : '#92400e' }}>{item.sub}</span>}
                  </div>
                  {idx === 0 && <span className="sn-urgent-badge">URGENT</span>}
                </div>
              ))
            )}
          </div>

          {/* Notifications */}
          <div className="sn-section-title" style={{ marginTop: '20px', color: '#78350f' }}>
            <Bell size={15} color="#92400e" strokeWidth={2.5} />
            NOTIFICATIONS
          </div>
          <div className="sn-list">
            {notifications.length === 0 ? (
              <div className="sn-item sn-notif-item">🔔 No notifications</div>
            ) : (
              notifications.map((item: any, idx: number) => (
                <div key={idx} className="sn-item sn-notif-item">
                  <div className="sn-notif-icon">
                    {idx === 0 ? '🔔' : (idx === 1 ? <span className="sn-live-icon">LIVE</span> : <span className="sn-dot-icon" />)}
                  </div>
                  <div className="sn-item-content">
                    <span className="sn-notif-msg">{item.message}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Weekly Calendar ─────────────────────────────────────────────────────────
const WeeklyCalendar = ({ schedules }: { schedules: any[] }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDayModal, setSelectedDayModal] = useState<{ date: Date; schedules: any[] } | null>(null);

  const today = new Date();

  const weekStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() - today.getDay() + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }), [weekStart]);

  const schedulesByDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    schedules.forEach((s) => {
      const d = parseScheduleDate(s.start_datetime);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [schedules]);

  return (
    <div className="weekly-cal-card">
      <div className="wcal-header">
        <h2 className="wcal-title">Weekly Schedule <span className="wcal-month">{weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></h2>
        <div className="wcal-nav">
          <span className="wcal-week-label">
            {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <button className="wcal-nav-btn" onClick={() => setWeekOffset((o) => o - 1)}><ChevronLeft size={16} /></button>
          <button className="wcal-nav-btn" onClick={() => setWeekOffset((o) => o + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="wcal-grid">
        {weekDays.map((date, i) => {
          const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          const daySchedules = schedulesByDay[key] || [];
          const isToday = date.toDateString() === today.toDateString();
          const dayName = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][date.getDay()];

          return (
            <div key={i} className={`wcal-col ${isToday ? 'wcal-today' : ''}`} onClick={() => daySchedules.length > 0 && setSelectedDayModal({ date, schedules: daySchedules })}>
              <div className="wcal-day-header">
                <span className="wcal-day-name">{dayName} {date.getDate()}</span>
                <span className={`wcal-day-num ${isToday ? 'wcal-day-num-today' : ''}`}>{date.getDate()}</span>
              </div>
              <div className="wcal-events">
                {daySchedules.length === 0 ? (
                  <span className="wcal-no-event">Off</span>
                ) : (
                  <div className="wcal-event-badge">{daySchedules.length} Event{daySchedules.length !== 1 ? 's' : ''}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="wcal-footer">Click any active date badge to review scheduled interview briefing.</div>

      {selectedDayModal && (
        <Modal
          open={!!selectedDayModal}
          onClose={() => setSelectedDayModal(null)}
          title={selectedDayModal.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        >
          <div className="cal-modal-list">
            {selectedDayModal.schedules.map((s: any, idx: number) => {
              const sessionName = s.session_name || 'Interview Session';
              const roundType = s.round_type || s.type || 'Technical Round';
              const interviewType = s.interview_type || 'One-on-One';
              const targetUsers = s.target_users?.length || s.target_users_count || s.users || s.candidates?.length || 0;

              const colors = ['#eff6ff', '#f0fdf4', '#fdf2f8', '#fffbeb', '#faf5ff'];
              const bgColor = colors[idx % colors.length];

              return (
                <div key={idx} className="cal-modal-item" style={{ backgroundColor: bgColor }}>
                  <div className="cal-modal-header">
                    <div>
                      <div className="cal-modal-title">{sessionName}</div>
                      <div className="cal-modal-round">{roundType}</div>
                    </div>
                    <span className="cal-modal-badge">{interviewType}</span>
                  </div>
                  <div className="cal-modal-details">
                    <div className="cal-modal-time">
                      <Clock size={14} />
                      {formatCalTime(s.start_datetime)} – {formatCalTime(s.end_datetime)}
                    </div>
                    <div className="cal-modal-users">
                      <Users size={14} />
                      {targetUsers} Target User{targetUsers !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Top Skills Chart ────────────────────────────────────────────────────────
const TopSkillsCard = ({ skillCounts }: { skillCounts: Record<string, number> }) => {
  const top = useMemo(() =>
    Object.entries(skillCounts || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([skill, count]) => ({ skill, count })),
    [skillCounts]
  );

  const maxCount = top.length > 0 ? Math.max(...top.map((t) => t.count), 1) : 1;

  return (
    <div className="dashboard-card">
      <div className="card-header-flex">
        <div>
          <h3 className="section-title">Top Skills</h3>
          <p className="section-subtitle">Most frequent skills listed by applicants</p>
        </div>
      </div>
      <div className="top-skills-list mt-10">
        {top.length === 0 ? (
          <div className="empty-state">No skill data yet</div>
        ) : (
          top.map((item, idx) => {
            const perc = (item.count / maxCount) * 100;
            return (
              <div key={item.skill} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
                <div style={{ width: '120px', flexShrink: 0 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>{item.skill}</span>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `max(8px, calc(${perc}% - 40px))` }}
                    transition={{ duration: 0.8, delay: idx * 0.06, ease: 'easeOut' }}
                    style={{ height: '10px', background: '#3b82f6', borderRadius: '4px' }}
                  />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b', marginLeft: '8px' }}>
                    {item.count}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Offer Outcome Donut ─────────────────────────────────────────────────────
const OfferOutcomeCard = ({ outcome }: { outcome: { offered: number; accepted: number; declined: number; not_answered: number } | undefined }) => {
  const data = useMemo(() => {
    const o = outcome || { offered: 0, accepted: 0, declined: 0, not_answered: 0 };
    return [
      { name: 'Accepted', value: o.accepted, color: '#4ade80' },
      { name: 'Declined', value: o.declined, color: '#f87171' },
      { name: 'Pending', value: o.not_answered, color: '#94a3b8' },
    ];
  }, [outcome]);

  const total = outcome?.offered || 0;

  return (
    <div className="dashboard-card" style={{ padding: '20px' }}>
      <div className="card-header-flex">
        <div>
          <h3 className="section-title">Offer Outcome</h3>
          <p className="section-subtitle">Acceptance vs Rejection breakdown</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="empty-state">No offer data yet</div>
      ) : (
        <div className="offer-outcome-wrapper">
          <div className="offer-donut" style={{ width: '160px', height: '160px', flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={2}
                  stroke="none"
                  dataKey="value"
                >
                  {data.filter(d => d.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ fontSize: '0.7rem', borderRadius: '8px' }} itemStyle={{ fontWeight: 600, color: '#334155' }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, marginBottom: '2px' }}>{total}</span>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Offers</span>
            </div>
          </div>

          <div className="offer-legend-simple">
            {data.map((d) => {
              const perc = total > 0 ? Math.round((d.value / total) * 100) : 0;
              return (
                <div key={d.name} className="offer-legend-simple-item">
                  <div className="legend-left">
                    <span className="offer-legend-sq" style={{ background: d.color }} />
                    <span className="offer-legend-name" style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>{d.name}</span>
                  </div>
                  <div className="legend-right">
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>{perc}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const companyName = authState.user?.company_name || authState.email || 'there';

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [jobMetrics, setJobMetrics] = useState<BaseMetricsAgg | null>(null);
  const [dashboardDetails, setDashboardDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [showTalentToast, setShowTalentToast] = useState(true);
  const [showProspectPopup, setShowProspectPopup] = useState(false);

  // Flag-driven list (backend sets has_prospective_candidates True on search
  // results, False once all moved to applied). Falls back to the legacy
  // recent-searches list for rows written before the flag existed.
  const prospectJobs: Array<{ opportunity_id: string; title?: string; prospective_count?: number }> = useMemo(() => {
    const flagged = dashboardDetails?.opportunities_with_prospects;
    let list: Array<{ opportunity_id: string; title?: string; prospective_count?: number }> = [];
    if (Array.isArray(flagged) && flagged.length > 0) {
      list = flagged;
    } else {
      const legacy = dashboardDetails?.completed_talent_searches;
      if (Array.isArray(legacy) && legacy.length > 0) {
        const seen = new Map<string, { opportunity_id: string; title?: string; prospective_count?: number }>();
        for (const s of legacy) {
          if (s?.opportunity_id && !seen.has(s.opportunity_id)) {
            seen.set(s.opportunity_id, { opportunity_id: s.opportunity_id, title: s.title });
          }
        }
        list = Array.from(seen.values());
      }
    }
    // Confirmed-zero never notifies; unknown (undefined/null) still does.
    return list.filter((j) => (j.prospective_count ?? 1) > 0);
  }, [dashboardDetails]);

  const goToProspectJob = (opportunityId: string) => {
    setShowProspectPopup(false);
    navigate(`/dashboard/job-applications/${opportunityId}?tab=applications`);
  };

  useEffect(() => {
    if (prospectJobs.length > 0) setShowTalentToast(true);
  }, [prospectJobs.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const offset = new Date().getTimezoneOffset();
        const sign = offset > 0 ? '-' : '+';
        const absOffset = Math.abs(offset);
        const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
        const minutes = String(absOffset % 60).padStart(2, '0');
        const offsetTime = `${sign}${hours}:${minutes}`;

        const [dashData, metricsData, detailsData] = await Promise.all([
          DashboardClient.getDashboardData(offsetTime).catch(() => null),
          JobDashboardClient.getMetrics().catch(() => null),
          JobDashboardClient.getDashboardDetails().catch(() => null),
        ]);

        if (dashData) setDashboardData(dashData.detail || dashData.data || dashData);
        if (metricsData) setJobMetrics(metricsData);
        if (detailsData) setDashboardDetails(detailsData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const trendData = useMemo(() => {
    if (!jobMetrics?.monthly_applications) return [];
    return Object.entries(jobMetrics.monthly_applications)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({ month, Applications: count }));
  }, [jobMetrics]);

  const DEFAULT_SOURCES = ['Referral', 'Website', 'Agency', 'Walk-in', 'LinkedIn', 'Job portal', 'Internal', 'Other'];

  const sourceData = useMemo(() => {
    const counts = jobMetrics?.source_counts || {};
    const allSourceKeys = [...DEFAULT_SOURCES];
    Object.keys(counts).forEach(k => {
      if (!allSourceKeys.includes(k)) {
        allSourceKeys.push(k);
      }
    });

    return allSourceKeys.map(name => ({
      name,
      value: counts[name] || 0,
    }));
  }, [jobMetrics]);

  const totalSourceCandidates = useMemo(() => sourceData.reduce((acc, curr) => acc + curr.value, 0), [sourceData]);

  const pipelineData = useMemo(() => {
    if (!jobMetrics?.stage_counts) return [];
    return DEFAULT_STAGES.map((stage) => ({
      stage,
      count: jobMetrics.stage_counts![stage] || 0,
    }));
  }, [jobMetrics]);

  const interviews = dashboardData?.interview_schedules || [];
  const assessments = dashboardData?.assessment_schedules || [];
  const schedules = useMemo(() => [
    ...interviews,
    ...assessments.map((a: any) => {
      const types = [];
      if (a.dimension_questions) types.push('Jozuna');
      if (a.programming_configs?.length > 0) types.push('Programming');
      if (a.industry_configs?.length > 0) {
        a.industry_configs.forEach((c: any) => {
          types.push(c.module_name || c.domain_type || 'SQL / Curriculum');
        });
      }
      const roundType = types.length > 0 ? types.join(', ') : 'Assessment';

      return {
        ...a,
        start_datetime: a.assessment_start_time,
        end_datetime: a.assessment_end_time,
        session_name: a.assessment_name || 'Assessment',
        round_type: roundType,
        interview_type: 'Assessment',
        target_users: a.target_candidates || []
      };
    })
  ], [interviews, assessments]);
  const actionRequired = dashboardDetails?.action_required || [];
  const notifications = dashboardDetails?.notifications || [];
  const topJobs = dashboardDetails?.top_jobs || [];
  const topSkills = dashboardDetails?.top_skills || [];
  const offerOutcome = dashboardDetails?.offer_outcome || {
    offered: 0,
    accepted: 0,
    declined: 0,
    not_answered: 0
  };

  const skillCountsRecord: Record<string, number> = useMemo(() => {
    if (Array.isArray(topSkills)) {
      const r: Record<string, number> = {};
      topSkills.forEach((item: any) => { if (item.skill) r[item.skill] = item.count; });
      return r;
    }
    return jobMetrics?.skill_counts || {};
  }, [topSkills, jobMetrics]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader isLoading={true} message="Loading dashboard..." />
      </div>
    );
  }

  const maxPipeline = Math.max(...pipelineData.map((d) => d.count), 1);

  return (
    <motion.div
      className="dashboard-page"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* ════════════ TOP SECTION (Left/Right Grid) ════════════ */}
      <div className="db-layout-top">

        {/* LEFT COLUMN */}
        <div className="db-top-left">
          {/* Welcome Greeting */}
          <div className="db-welcome-card">
            <div className="db-welcome-left">
              <div className="db-welcome-greeting">👋 Good day,</div>
              <div className="db-welcome-name">{companyName}</div>
            </div>
            <div className="db-welcome-right">
              <div className="db-welcome-date">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="db-stats-row">
            <FrostedGlassStatCard
              icon={<Users size={20} />}
              heading="Total Applications"
              count={(jobMetrics?.total_applications ?? 0).toLocaleString()}
              accent="purple"
              onClick={() => navigate('/dashboard/job-opportunities')}
            />
            <FrostedGlassStatCard
              icon={<FolderOpen size={20} />}
              heading="Active Jobs"
              count={(jobMetrics?.open_opportunities ?? 0).toLocaleString()}
              accent="blue"
              onClick={() => navigate('/dashboard/job-opportunities')}
            />
            <FrostedGlassStatCard
              icon={<Clock size={20} />}
              heading="Scheduled Events"
              count={(dashboardData ? dashboardData.total_active_schedules : 0).toLocaleString()}
              accent="pink"
              onClick={() => setShowSchedulePicker(true)}
            />
            <FrostedGlassStatCard
              icon={<UserCheck size={20} />}
              heading="Total Hires"
              count={(jobMetrics?.total_hires ?? 0).toLocaleString()}
              accent="green"
            />
          </div>

          <SchedulePickerModal
            open={showSchedulePicker}
            onClose={() => setShowSchedulePicker(false)}
            onSelect={(target) => {
              setShowSchedulePicker(false);
              navigate(target === 'interviews' ? '/dashboard/interviews' : '/dashboard/assessments');
            }}
          />

          {/* Weekly Calendar */}
          <WeeklyCalendar schedules={schedules} />
        </div>

        {/* RIGHT COLUMN */}
        <div className="db-top-right">
          <NoticeBoardCard actionRequired={actionRequired} notifications={notifications} />
        </div>
      </div>

      {/* ════════════ ROW 2: CHARTS ════════════ */}
      <div className="db-layout-row-3">
        {/* Pipeline */}
        <div className="dashboard-card">
          <div className="card-header-flex">
            <div>
              <h3 className="section-title">Global Recruitment Pipeline</h3>
              <p className="section-subtitle">Applied → Onboarded progression across all jobs</p>
            </div>
          </div>
          <div className="custom-pipeline-container mt-10">
            {pipelineData.map((item, index) => (
              <div key={item.stage} className="pipeline-progress-row">
                <div className="pipeline-label-row">
                  <span className="pipeline-stage">{item.stage}</span>
                  <span className="pipeline-count">{item.count.toLocaleString()}</span>
                </div>
                <div className="pipeline-track">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item.count / maxPipeline) * 100}%` }}
                    transition={{ duration: 0.9, ease: 'easeOut', delay: index * 0.05 }}
                    className="pipeline-fill"
                    style={{ background: COLORS[index % COLORS.length] }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Applicant Volume */}
        <div className="dashboard-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="card-header-flex">
            <div>
              <h3 className="section-title">Applicant Volume</h3>
              <p className="section-subtitle">Monthly application velocity</p>
            </div>
          </div>
          <div className="chart-wrapper-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickMargin={10} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickMargin={10} />
                <RechartsTooltip contentStyle={{ fontSize: '0.75rem', borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} itemStyle={{ fontSize: '0.75rem', color: '#0f172a', fontWeight: 600 }} />
                <Area type="monotone" dataKey="Applications" stroke="#0d9488" strokeWidth={3} fillOpacity={1} fill="url(#colorApps)" activeDot={{ r: 6, fill: '#0d9488', stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Applicant Source */}
        <div className="dashboard-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="card-header-flex">
            <div>
              <h3 className="section-title">Applicant Source</h3>
              <p className="section-subtitle">Primary candidate acquisition channels</p>
            </div>
          </div>
          {sourceData.length === 0 ? (
            <div className="empty-state" style={{ margin: 'auto', textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '0.85rem' }}>
              No applicant source data yet
            </div>
          ) : (
            <>
              <div className="chart-wrapper-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sourceData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value">
                      {sourceData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        fontSize: '0.75rem',
                        color: '#475569',
                        padding: '8px 12px'
                      }}
                      itemStyle={{ color: '#0f172a', fontWeight: 700 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-center">
                  <span className="donut-num">{totalSourceCandidates.toLocaleString()}</span>
                  <span className="donut-txt">CANDIDATES</span>
                </div>
              </div>
              <div className="source-legend-wrap">
                {sourceData.map((item, idx) => {
                  return (
                    <div key={item.name} className="source-legend-item">
                      <span className="legend-dot" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="legend-name">{item.name}</span>
                      <span style={{ color: item.value > 0 ? '#0f172a' : '#94a3b8', fontSize: '0.72rem', fontWeight: item.value > 0 ? 700 : 500 }}>
                        ({item.value})
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ════════════ ROW 3: LISTS ════════════ */}
      <div className="db-layout-row-3">
        {/* Top Performing Jobs */}
        <div className="dashboard-card">
          <div className="card-header-flex">
            <div>
              <h3 className="section-title">Top Performing Jobs</h3>
              <p className="section-subtitle">Highest applicant traction by department</p>
            </div>
          </div>
          <div className="simple-list mt-10">
            {topJobs.length === 0 ? (
              <div className="empty-state">No jobs data yet</div>
            ) : (
              topJobs.map((job: any, idx: number) => (
                <div key={idx} className="list-item-clean">
                  <div className="list-item-content">
                    <h4>{job.title}</h4>
                    <div className="list-item-tags">
                      {(() => {
                        const hash = (job.department || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
                        const tagColors = [
                          { bg: '#eff6ff', color: '#2563eb' },
                          { bg: '#f0fdf4', color: '#16a34a' },
                          { bg: '#fdf2f8', color: '#db2777' },
                          { bg: '#fffbeb', color: '#d97706' },
                          { bg: '#faf5ff', color: '#9333ea' },
                          { bg: '#fff1f2', color: '#e11d48' },
                          { bg: '#f0fdfa', color: '#0d9488' }
                        ];
                        const tColor = tagColors[hash % tagColors.length];
                        return (
                          <span style={{ backgroundColor: tColor.bg, color: tColor.color, padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600 }}>
                            {job.department}
                          </span>
                        );
                      })()}
                      {/* <span className="tag-text">Open {Math.floor(Math.random() * 30) + 1} days</span> */}
                    </div>
                  </div>
                  <div className="list-item-value">
                    <span className="value-number text-green">{job.applicants.toLocaleString()}</span>
                    <span className="value-label">applicants</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Skills */}
        <TopSkillsCard skillCounts={skillCountsRecord} />

        {/* Offer Outcome */}
        <OfferOutcomeCard outcome={offerOutcome} />
      </div>

      <AnimatePresence>
        {prospectJobs.length > 0 && showTalentToast && (
          <JobNotificationToast
            title={prospectJobs.length === 1 ? "Talent Search Completed" : `${prospectJobs.length} Jobs Have Prospects`}
            message={
              prospectJobs.length === 1
                ? <>New prospective candidates found for <span style={{ color: '#052e16', fontWeight: 600 }}>{prospectJobs[0].title || 'a job'}</span>.</>
                : <>Multiple jobs have prospective candidates. Click to review.</>
            }
            icon={<Search size={20} />}
            actionLabel={prospectJobs.length === 1 ? "Review" : "View all"}
            onClick={() => {
              if (prospectJobs.length === 1) {
                goToProspectJob(prospectJobs[0].opportunity_id);
              } else {
                setShowProspectPopup(true);
              }
            }}
            onClose={() => setShowTalentToast(false)}
          />
        )}
      </AnimatePresence>

      <Modal
        open={showProspectPopup}
        onClose={() => setShowProspectPopup(false)}
        title="Jobs with prospective candidates"
        width="560px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {prospectJobs.map((job) => (
            <div
              key={job.opportunity_id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '12px', padding: '12px 14px', borderRadius: '12px',
                border: '1px solid #e2e8f0', background: '#f8fafc',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {job.title || 'Untitled job'}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {(job.prospective_count ?? 0) > 0
                    ? `${job.prospective_count} prospective candidate${job.prospective_count === 1 ? '' : 's'}`
                    : 'Prospective candidates available'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => goToProspectJob(job.opportunity_id)}
                style={{
                  flexShrink: 0, padding: '6px 16px', borderRadius: '9999px',
                  border: '1px solid rgba(21,128,61,0.4)', background: '#fff',
                  color: '#052e16', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                View
              </button>
            </div>
          ))}
        </div>
      </Modal>
    </motion.div>
  );
};

export default DashboardPage;